import type React from "react";
import { useEffect, useMemo, useState } from "react";
import ErrorMessage from "../../shared/elements/ErrorMessage";
import type { HttpMethod } from "../../shared/models/HttpMethod";
import LoadingOverlay from "../shared/LoadingOverlay";
import { useAdminQueries } from "./AdminClient";

export function useQueries<T>({
  paths,
  updated: updatedIn,
}: {
  paths: string[];
  updated?: Date;
}) {
  const { reload, error, loading, data } = useAdminQueries<T>(paths);

  const [updated, setUpdated] = useState<Date | null>(null);
  const [lastFetched, setLastFetched] = useState(new Date());

  useEffect(() => {
    if (data) setLastFetched(new Date());
  }, [data]);

  useEffect(() => {
    if (updatedIn) setUpdated(updatedIn);
  }, [updatedIn]);

  useEffect(() => {
    if (updated) reload();
  }, [updated, reload]);

  return useMemo(
    () => ({
      error,
      loading,
      data,
      updated: lastFetched,
      refresh: () => setUpdated(new Date()),
    }),
    [error, loading, data, lastFetched],
  );
}

export type QueryResult<T> = {
  error: { message?: string } | null;
  loading: boolean;
  data: T | null;
  refresh: () => void;
  updated?: Date;
};

export function useQuery<T>({
  path,
  updated,
}: {
  path: string;
  updated?: Date;
}): QueryResult<T> {
  const paths = useMemo(() => [path], [path]);

  const queryPages = useQueries<T>({ paths, updated });

  const [data, setData] = useState<T | null>(null);

  useEffect(() => {
    if (queryPages.data) {
      const match = queryPages.data.find((x) => x.path === path);

      if (match) setData(match.data);
    } else {
      setData(null);
    }
  }, [path, queryPages]);

  return useMemo(() => ({ ...queryPages, data }), [queryPages, data]);
}

export function QueryResultView<T>({
  queryPage: { error, loading, data, refresh, updated },
  children,
  showOverlay = true,
}: {
  queryPage: QueryResult<T>;
  children: () => React.ReactNode;
  showOverlay?: boolean;
}) {
  if (error) {
    return (
      <div>
        <ErrorMessage>{error.message || error.toString()}</ErrorMessage>
        <button type="button" onClick={refresh}>
          Retry
        </button>
      </div>
    );
  }

  if (loading && !data) {
    return <LoadingOverlay loading={loading} />;
  }

  if (!data) {
    return (
      <div>
        <span>Ingen data!</span>
      </div>
    );
  }

  return (
    <>
      {children()}
      <div>
        <button type="button" onClick={refresh}>
          Refresh
        </button>
        {
          <small style={{ margin: "0 .5em" }}>
            Updated: {updated?.toLocaleTimeString()}
          </small>
        }
        {loading && showOverlay ? <LoadingOverlay loading={loading} /> : null}
      </div>
    </>
  );
}

export type QuerySuccessResult<T> = QueryResult<T> & {
  data: T;
  error: null;
};

export type QueryProps<T> = {
  path: string;
  method?: HttpMethod;
  children: (queryPage: QuerySuccessResult<T>) => JSX.Element;
  updated?: Date;
};

export default function Query<T>({ path, children, updated }: QueryProps<T>) {
  const queryPage = useQuery<T>({
    path,
    updated,
  });

  return (
    <QueryResultView<T> queryPage={queryPage}>
      {() => children(queryPage as QuerySuccessResult<T>)}
    </QueryResultView>
  );
}
